import Container from "@/components/Container/Container";
import Section from "@/components/Section/Section";
import PageHero from "@/components/PageHero/PageHero";
import CTASection from "@/components/CTASection/CTASection";
import styles from "./AboutPage.module.css";

const focusItems = [
  "Understanding how past experiences shape present patterns",
  "Helping clients regulate their nervous systems and feel more grounded",
  "Exploring relational dynamics within family and social systems",
  "Identifying practical, achievable steps toward change",
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About"
        title="A grounded, integrative approach to therapy."
        intro="I am a Licensed Marriage and Family Therapist with over a decade of clinical experience supporting individuals, couples, families, and clinicians in training."
        accent="bloom"
      />

      <Section spacing="loose">
        <Container width="default" className={styles.bio}>
          <p>
            My work is grounded in a deep belief that people are capable of meaningful change
            when they feel understood, supported, and connected to their own internal wisdom.
          </p>
          <p>
            Throughout my career I have worked in both community mental health and private
            practice settings, supporting clients navigating trauma, anxiety, relationship
            challenges, life transitions, and complex family dynamics. In addition to my
            clinical practice, I supervise and mentor therapists, helping them grow their
            clinical confidence and develop thoughtful, ethical, and effective therapeutic
            practices.
          </p>
          <p>
            My approach is collaborative, compassionate, and practical. Therapy with me is not
            about &ldquo;fixing&rdquo; you&mdash;it is about helping you understand your patterns,
            reconnect with your strengths, and create the kind of life and relationships you want.
          </p>
        </Container>
      </Section>

      <Section tone="soft" spacing="loose">
        <Container width="default" className={styles.approach}>
          <span className="eyebrow">My therapeutic approach</span>
          <h2>An integrative framework that addresses both emotion and physiology.</h2>
          <p>
            I practice from an integrative framework that combines brain-based, somatic,
            relational, and solution-focused approaches. This allows therapy to address both the
            emotional and physiological aspects of healing&mdash;not just thoughts or behaviors.
          </p>
          <ul className={styles.focusList}>
            {focusItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className={styles.closing}>
            Together we work to build insight, resilience, and new possibilities for growth.
          </p>
        </Container>
      </Section>

      <Section spacing="loose">
        <Container width="default" className={styles.story}>
          <span className="eyebrow">Why I became a therapist</span>
          <h2>I have always been curious about how people make meaning of their experiences.</h2>
          <p>
            Over time I found myself drawn to work that supports people through difficult
            transitions, trauma, and the complexities of relationships. Throughout my career I
            have worked in community mental health and private practice settings, supporting
            individuals, couples, and families navigating significant challenges.
          </p>
          <p>
            These experiences have shaped my belief that healing happens through a combination
            of understanding our stories, regulating our nervous systems, and creating new
            relational experiences that foster safety and growth.
          </p>
          <p>
            Therapy offers a space where people can pause, reflect, and reconnect with parts of
            themselves that may have been overshadowed by stress, trauma, or life circumstances.
            My role is to provide a thoughtful and grounded environment where clients can explore
            these experiences while building practical tools for change.
          </p>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
